import React, { useEffect, useMemo, useState } from "react";
import { Modal, Input, Select, Textarea, Button, StatusBadge, toast } from "@/components/ds";
import { api, formatApiError, type AvailabilitySlot } from "@/lib/api";
import { Field } from "../onboarding/_ui";
import { isoDate } from "./constants";

export function AppointmentForm({ initialDate, onClose, onSaved }: {
  initialDate?: Date | null; onClose: () => void; onSaved: () => void;
}) {
  const [types, setTypes] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [intake, setIntake] = useState<any[]>([]);
  const [typeId, setTypeId] = useState("");
  const [contactId, setContactId] = useState("");
  const [date, setDate] = useState(isoDate(initialDate ?? new Date()));
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [slots, setSlots] = useState<AvailabilitySlot[] | null>(null);
  const [outOfArea, setOutOfArea] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.atList().then((t) => { setTypes(t as any[]); const active = (t as any[]).filter((x) => x.active); if (active[0]) setTypeId(active[0].id); }).catch((e) => toast.error(formatApiError(e)));
    api.crmContacts().then((c) => setContacts(c as any[])).catch(() => {});
    api.intakeList().then((q) => setIntake((q as any[]).filter((x) => x.active !== false))).catch(() => {});
  }, []);

  const requiredIntake = useMemo(() => intake.filter((q) => q.required), [intake]);

  const loadSlots = async () => {
    if (!typeId || !date) return;
    setLoadingSlots(true); setSelectedSlot(""); setSlots(null); setOutOfArea(false);
    try {
      const r = await api.schedAvailability({ appointment_type_id: typeId, date, postal_code: postalCode || null, city: city || null });
      setSlots(r.slots); setOutOfArea(r.out_of_service_area);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoadingSlots(false); }
  };

  useEffect(() => { if (typeId && date) loadSlots(); /* eslint-disable-next-line */ }, [typeId, date, postalCode, city]);

  const save = async () => {
    if (!typeId) { toast.error("Choose an appointment type."); return; }
    if (!contactId) { toast.error("Choose a contact."); return; }
    if (!selectedSlot) { toast.error("Pick an available time slot."); return; }
    for (const q of requiredIntake) {
      if (!(answers[q.id] ?? "").trim()) { toast.error(`Please answer: ${q.label}`); return; }
    }
    setSaving(true);
    try {
      await api.schedCreateAppointment({
        appointment_type_id: typeId, contact_id: contactId, start_at: selectedSlot,
        intake_answers: answers, notes: notes.trim() || null,
        postal_code: postalCode || null, city: city || null,
      });
      toast.success("Appointment booked"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="New appointment" description="Only conflict-free, open times are offered." testid="appointment-form">
      <div className="max-h-[74vh] space-y-4 overflow-y-auto pr-1">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Appointment type" required testid="field-appt-type">
            <Select value={typeId} onChange={setTypeId} testid="appt-type-select"
              options={types.filter((t) => t.active).map((t) => ({ value: t.id, label: `${t.name} · ${t.duration_minutes}m` }))} />
          </Field>
          <Field label="Contact" required testid="field-appt-contact">
            <Select value={contactId} onChange={setContactId} testid="appt-contact-select"
              options={contacts.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name ?? ""}`.trim() + (c.phone ? ` · ${c.phone}` : "") }))} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Date" required testid="field-appt-date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appt-date" /></Field>
          <Field label="City (for service area)" testid="field-appt-city"><Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Chicago" data-testid="appt-city" /></Field>
          <Field label="Postal code" testid="field-appt-postal"><Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="60601" data-testid="appt-postal" /></Field>
        </div>

        <div data-testid="appt-slots">
          <p className="mb-2 text-sm font-semibold text-slate-800">Available times</p>
          {outOfArea ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="appt-out-of-area">
              This location is outside your configured service area.
            </div>
          ) : loadingSlots ? (
            <p className="py-3 text-sm text-slate-400">Loading available times…</p>
          ) : slots === null ? (
            <p className="py-3 text-sm text-slate-400">Select a type and date to see open times.</p>
          ) : slots.length === 0 ? (
            <p className="py-3 text-sm text-slate-500" data-testid="appt-no-slots">No open times for this day. Try another date.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((s) => (
                <button key={s.start_at} onClick={() => setSelectedSlot(s.start_at)} data-testid={`slot-${s.local_time}`}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${selectedSlot === s.start_at ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}>
                  {s.local_time}
                </button>
              ))}
            </div>
          )}
        </div>

        {requiredIntake.length > 0 && (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="appt-intake">
            <p className="text-sm font-semibold text-slate-800">Required intake</p>
            {requiredIntake.map((q) => (
              <Field key={q.id} label={q.label} required testid={`field-intake-${q.id}`}>
                <Input value={answers[q.id] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} data-testid={`intake-${q.id}`} />
              </Field>
            ))}
          </div>
        )}

        <Field label="Notes" testid="field-appt-notes"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes…" data-testid="appt-notes" /></Field>

        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <div>{selectedSlot ? <StatusBadge tone="blue">Selected time chosen</StatusBadge> : <span className="text-xs text-slate-400">No time selected</span>}</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving || !selectedSlot} data-testid="appt-save">{saving ? "Booking…" : "Book appointment"}</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
